import classNames from 'classnames';
import {
  type FormEvent,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useState,
} from 'react';

import { Button, ButtonSize } from '@atlas/components';
import type { Field, Rule } from '@atlas/react-forms';
import { Translation } from 'utils/translations';

import Errors from './Errors';
import ErrorsList from './ErrorsList';

import styles from './Form.module.scss';

type Props = PropsWithChildren & {
  onSubmit: (e: FormEvent) => void;
  globalErrors: Rule[];
  fields: Field[];
  fieldErrors: Record<string, string[]>;
  beforeSubmitButton: ReactNode;
  afterSubmitButton: ReactNode;
  submitButtonText: string;
  showErrorFields?: boolean;
};

const FormContainer = ({
  onSubmit,
  globalErrors,
  fields,
  beforeSubmitButton,
  afterSubmitButton,
  submitButtonText,
  fieldErrors,
  children,
  showErrorFields = false,
}: Props) => {
  const [errorFields, setErrorFields] = useState<string[]>([]);

  const handleErrorFields = useCallback(() => {
    setErrorFields(
      fields.reduce((fieldsToReturn: string[], field: Field) => {
        const currentFieldErrors = fieldErrors[field.name];

        if (!currentFieldErrors || currentFieldErrors.length === 0) {
          return fieldsToReturn;
        }

        return fieldsToReturn.concat(field.label);
      }, []),
    );
  }, [fieldErrors, fields]);

  const onSubmitHandler = async (e: FormEvent) => {
    handleErrorFields();
    onSubmit(e);
  };

  return (
    <form
      autoComplete="off"
      className={classNames(styles.form, {
        [styles['with-errors']]: globalErrors.length,
      })}
    >
      {children}
      {globalErrors && (
        <Errors errors={globalErrors} className={styles.errors} />
      )}
      {showErrorFields && (
        <ErrorsList className={styles['errors-list']} errors={errorFields} />
      )}
      {beforeSubmitButton}
      <Button
        type="submit"
        data-testid="submit"
        size={ButtonSize.big}
        onClick={onSubmitHandler}
      >
        <Translation message={submitButtonText} />
      </Button>
      {afterSubmitButton}
    </form>
  );
};

export default FormContainer;
